import { useState, useEffect } from 'preact/hooks';
import { api, type TeamInviteLink } from '../services/api';

interface MemberInviteLinksSectionProps {
  className?: string;
}

export function MemberInviteLinksSection({ className = '' }: MemberInviteLinksSectionProps) {
  const [inviteLinks, setInviteLinks] = useState<TeamInviteLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  const [newLinkData, setNewLinkData] = useState({
    label: '',
    expires_at: '',
    max_uses: ''
  });

  useEffect(() => {
    loadInviteLinks();
  }, []);

  const loadInviteLinks = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getTeamInviteLinks();
      if (response.success) {
        setInviteLinks(response.invites);
      } else {
        setError('Failed to load member invite links');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load member invite links');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateLink = async (e: Event) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const data: any = {
        label: newLinkData.label || undefined,
      };

      if (newLinkData.expires_at) {
        data.expires_at = newLinkData.expires_at;
      }

      if (newLinkData.max_uses) {
        data.max_uses = parseInt(newLinkData.max_uses);
      }

      const response = await api.createTeamInviteLink(data);
      if (response.success) {
        await loadInviteLinks();
        setNewLinkData({ label: '', expires_at: '', max_uses: '' });
        setShowCreateForm(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create member invite link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this member invite link?')) return;
    try {
      await api.deactivateTeamInviteLink(id);
      await loadInviteLinks();
    } catch (err: any) {
      alert(err.message || 'Failed to deactivate invite link');
    }
  };

  const getFullInviteUrl = (token: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/join-team?token=${token}`;
  };

  const copyToClipboard = async (token: string, linkId: string) => {
    const fullUrl = getFullInviteUrl(token);

    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedLinkId(linkId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLinkId(linkId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    }
  };

  const isExpired = (expiresAt: string | undefined) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Member Invite Links</h2>
          <p className="text-sm text-gray-500 mt-1">
            Share these links to invite team members and volunteers to access your church administration.
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn-primary self-start sm:self-auto flex items-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          New Member Link
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Create Link Modal/Card */}
      {showCreateForm && (
        <div className="mb-6 p-5 border border-primary-100 bg-primary-50/40 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">Create Member Invite Link</h3>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="text-gray-400 hover:text-gray-600 text-sm font-medium"
            >
              ✕
            </button>
          </div>
          <form onSubmit={handleCreateLink} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Label / Team Description
                </label>
                <input
                  type="text"
                  value={newLinkData.label}
                  onInput={(e) => setNewLinkData({ ...newLinkData, label: (e.target as HTMLInputElement).value })}
                  placeholder="e.g., Sunday School Volunteers"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Expiration Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={newLinkData.expires_at}
                  onInput={(e) => setNewLinkData({ ...newLinkData, expires_at: (e.target as HTMLInputElement).value })}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Max Uses (Optional)
                </label>
                <input
                  type="number"
                  min="1"
                  value={newLinkData.max_uses}
                  onInput={(e) => setNewLinkData({ ...newLinkData, max_uses: (e.target as HTMLInputElement).value })}
                  placeholder="e.g., 10"
                  className="input"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={isCreating}
                className="btn-primary disabled:opacity-50"
              >
                {isCreating ? 'Creating…' : 'Generate Link'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-sm text-gray-600">Loading member invite links…</span>
        </div>
      ) : inviteLinks.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
          <div className="w-12 h-12 bg-primary-50 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <p className="text-gray-600 font-medium">No member invite links created yet</p>
          <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
            Generate a link to easily onboard staff and volunteers into your church team without sending individual emails.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="mt-4 text-primary-600 hover:text-primary-700 text-sm font-semibold inline-flex items-center gap-1"
          >
            + Create your first member invite link
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {inviteLinks.map((link) => {
            const expired = isExpired(link.expires_at);
            const inactive = !link.is_active || expired;
            const fullUrl = getFullInviteUrl(link.token);
            const isCopied = copiedLinkId === link.id;

            return (
              <div
                key={link.id}
                className={`border rounded-xl p-4 transition-colors ${
                  inactive ? 'bg-gray-50/80 border-gray-200 opacity-80' : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">
                        {link.label || 'General Team Invite'}
                      </span>
                      {link.is_active && !expired ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : expired ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Expired
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Inactive
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {link.usage_count} {link.usage_count === 1 ? 'member joined' : 'members joined'}
                        {link.max_uses ? ` (max ${link.max_uses})` : ''}
                      </span>
                    </div>

                    <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                      <span>Created {formatDate(link.created_at)}</span>
                      {link.expires_at && (
                        <span>Expires {formatDate(link.expires_at)}</span>
                      )}
                    </div>

                    {/* URL preview */}
                    <div className="mt-2 flex items-center gap-2 max-w-xl">
                      <input
                        type="text"
                        readOnly
                        value={fullUrl}
                        className="bg-gray-50 border border-gray-200 rounded px-2.5 py-1 text-xs text-gray-600 font-mono w-full truncate select-all"
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 self-end lg:self-center">
                    <button
                      type="button"
                      onClick={() => copyToClipboard(link.token, link.id)}
                      disabled={inactive}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isCopied
                          ? 'bg-green-600 text-white'
                          : inactive
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800'
                      }`}
                      title={inactive ? 'Link is inactive' : 'Copy member invite link to clipboard'}
                    >
                      {isCopied ? (
                        <>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                          Copied!
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                          </svg>
                          Copy Link
                        </>
                      )}
                    </button>

                    {link.is_active && !expired && (
                      <button
                        type="button"
                        onClick={() => handleDeactivate(link.id)}
                        className="text-xs text-red-500 hover:text-red-700 px-2 py-1.5 transition-colors"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
